import Head from 'next/head'

export interface SeoProps {
  title: string
  description: string
  // Only when the canonical is elsewhere; the _app default covers the route.
  canonical?: string
  // Relative paths resolve against `canonical`.
  image?: string
  imageAlt?: string
  imageWidth?: string | number
  imageHeight?: string | number
  type?: string // og:type, default "website"
  siteName?: string // og:site_name, default "ATM.com"
  themeColor?: string // mobile UI tint + PWA theme, default "#3968DE"
}

const DEFAULT_SITE_NAME = 'ATM.com'
const DEFAULT_THEME_COLOR = '#3968DE'
const TWITTER_HANDLE = '@atmdotcom'

function toAbsolute(
  image: string | undefined,
  canonical: string | undefined,
): string | undefined {
  if (!image) return undefined
  if (/^https?:\/\//.test(image) || !canonical) return image
  try {
    return new URL(image, canonical).toString()
  } catch {
    return image
  }
}

// Single source of truth for document head meta — shared by the website and the
// web-app. Owns its own <Head> (next/head) so the tags are DIRECT children that
// Next collects + dedupes on SSR *and* client-side navigation. Render it
// standalone (NOT inside another <Head>); a per-page <Seo> overrides an
// app-level default via the matching keys (last one wins).
export function Seo({
  title,
  description,
  canonical,
  image,
  imageAlt = '',
  imageWidth,
  imageHeight,
  type = 'website',
  siteName = DEFAULT_SITE_NAME,
  themeColor = DEFAULT_THEME_COLOR,
}: SeoProps) {
  const ogImage = toAbsolute(image, canonical)

  return (
    <Head>
      <title key="title">{title}</title>
      <meta key="description" name="description" content={description} />
      {canonical && <link key="canonical" rel="canonical" href={canonical} />}
      <meta key="theme-color" name="theme-color" content={themeColor} />

      {/* Open Graph */}
      <meta key="og:type" property="og:type" content={type} />
      <meta key="og:site_name" property="og:site_name" content={siteName} />
      <meta key="og:title" property="og:title" content={title} />
      <meta
        key="og:description"
        property="og:description"
        content={description}
      />
      {canonical && <meta key="og:url" property="og:url" content={canonical} />}
      {ogImage && (
        <meta key="og:image" property="og:image" content={ogImage} />
      )}
      {ogImage && imageAlt && (
        <meta key="og:image:alt" property="og:image:alt" content={imageAlt} />
      )}
      {imageWidth && (
        <meta
          key="og:image:width"
          property="og:image:width"
          content={String(imageWidth)}
        />
      )}
      {imageHeight && (
        <meta
          key="og:image:height"
          property="og:image:height"
          content={String(imageHeight)}
        />
      )}

      {/* Twitter */}
      <meta
        key="twitter:card"
        name="twitter:card"
        content="summary_large_image"
      />
      <meta key="twitter:site" name="twitter:site" content={TWITTER_HANDLE} />
      <meta key="twitter:title" name="twitter:title" content={title} />
      <meta
        key="twitter:description"
        name="twitter:description"
        content={description}
      />
      {ogImage && (
        <meta key="twitter:image" name="twitter:image" content={ogImage} />
      )}
      {ogImage && imageAlt && (
        <meta
          key="twitter:image:alt"
          name="twitter:image:alt"
          content={imageAlt}
        />
      )}

      {/* Icons + manifest (per-origin, root-relative) */}
      <link key="icon-ico" rel="icon" href="/favicon.ico" sizes="any" />
      <link
        key="icon-svg"
        rel="icon"
        type="image/svg+xml"
        href="/favicon-vector.svg"
      />
      <link
        key="apple-touch"
        rel="apple-touch-icon"
        href="/favicon-180x180.png"
      />
      <link key="manifest" rel="manifest" href="/manifest.json" />
    </Head>
  )
}
